import React, { useState } from "react";
import { User, X, Lock, Loader2, Check, Crown, Calendar } from "lucide-react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../config/firebase"; // 引用新設定
import { appId } from "../../config/constants"; // 引用新設定

const MemberProfileModal = ({ userData, onClose }) => {
  const [realName, setRealName] = useState(userData?.realName || userData?.name || "");
  const [nickname, setNickname] = useState(userData?.nickname || userData?.name || "");
  const [loading, setLoading] = useState(false);

  const planId = (userData?.planId || 'A').toUpperCase();
  const planNameDisplay = { F: "體驗會員", A: "基本會員", B: "高級會員", C: "鑽石會員" };
  const planColor = { 
      F: "bg-gray-100 text-gray-600 border border-gray-200", 
      A: "bg-slate-100 text-slate-600", 
      B: "bg-blue-100 text-blue-600", 
      C: "bg-amber-100 text-amber-600" 
  };
  const expiryDateString = userData?.expiryDate ? userData.expiryDate.toDate().toLocaleDateString() : "永久有效";

  const handleSave = async () => {
    if (!nickname.trim()) return alert("暱稱不能留空喔！");
    setLoading(true);
    try {
      const userRef = doc(db, "artifacts", appId, "public", "data", "registered_users", userData.email.toLowerCase());
      await updateDoc(userRef, {
        nickname: nickname.trim(),
        realName: userData.realName || userData.name, 
        name: userData.realName || userData.name 
      });
      alert("✅ 個人資料已更新！");
      onClose();
    } catch (e) {
      alert("儲存失敗：" + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in" onClick={onClose}>
      <div className="bg-white rounded-[2.5rem] w-full max-w-sm max-h-[90vh] overflow-y-auto shadow-2xl relative no-scrollbar" onClick={e => e.stopPropagation()}>
        <div className="bg-slate-900 p-6 text-white relative shrink-0">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-all"><X size={18}/></button>
            <div className="flex flex-col items-center mt-2">
                <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mb-3 backdrop-blur-md border border-white/20">
                    <Crown size={40} className="text-yellow-400 fill-yellow-400" />
                </div>
                <h3 className="text-xl font-black tracking-wide">{nickname}</h3>
                <div className={`mt-2 px-3 py-1 rounded-full text-xs font-bold ${planColor[planId]}`}>
                    {planNameDisplay[planId]}
                </div>
            </div>
        </div>

        <div className="p-6 space-y-6">
            <div className="bg-white border-2 border-orange-100 rounded-2xl p-4 space-y-3 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-orange-100 text-orange-600 text-[10px] font-bold px-2 py-1 rounded-bl-xl">編輯資料</div>
                
                <div>
                    <label className="text-[10px] font-bold text-slate-400 ml-1">真實姓名 (鎖定)</label>
                    <div className="relative">
                        <input type="text" value={realName} disabled className="w-full p-2 bg-slate-100 border border-slate-200 rounded-lg text-sm font-bold text-slate-500 cursor-not-allowed"/>
                        <Lock size={14} className="absolute right-3 top-2.5 text-slate-400"/>
                    </div>
                </div>

                <div>
                    <label className="text-[10px] font-bold text-orange-500 ml-1">顯示暱稱 (可修改)</label>
                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            value={nickname} 
                            onChange={(e) => setNickname(e.target.value)}
                            maxLength={8}
                            className="flex-1 p-2 bg-white border-2 border-orange-100 focus:border-orange-400 rounded-lg text-sm font-bold text-slate-800 outline-none transition-all"
                        />
                        <button 
                            onClick={handleSave} 
                            disabled={loading}
                            className="bg-orange-500 text-white px-3 rounded-lg font-bold text-xs hover:bg-orange-600 transition-colors shadow-md flex items-center justify-center min-w-[60px]"
                        >
                            {loading ? <Loader2 size={14} className="animate-spin"/> : "儲存"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-sm font-bold text-slate-500 flex items-center gap-2"><Calendar size={16}/> 會籍效期</span>
                <span className="text-sm font-black text-slate-800">{expiryDateString}</span>
            </div>
   
            <div className="space-y-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">✨ 生涯解題成就 ✨</p>
                <div className="grid grid-cols-3 gap-2">
                    <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100 text-center">
                        <p className="text-[10px] text-slate-400 font-bold mb-1">AI解析</p>
                        <p className="text-lg font-black text-slate-700">{userData?.totalQuestions || 0}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100 text-center">
                        <p className="text-[10px] text-slate-400 font-bold mb-1">舉一反三</p>
                        <p className="text-lg font-black text-slate-700">{userData?.totalQuizzes || 0}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100 text-center">
                        <p className="text-[10px] text-slate-400 font-bold mb-1">觀念整理</p>
                        <p className="text-lg font-black text-slate-700">{userData?.totalConcepts || 0}</p>
                    </div>
                </div>
            </div>
            
            <div className="text-center text-[10px] text-slate-300">加入時間：{userData?.createdAt?.toDate().toLocaleDateString()}</div>
        </div>
      </div>
    </div>
  );
};

export default MemberProfileModal;
